<script>
import RawData from "@/components/widgets/raw-data.vue";

export default {
    name: 'SubsidiaryLedgerSelectionModal',
    components: {
        RawData,
    },
    data() {
        return {
            subsidiaryLedgers: [],
            slTypes: [],
            filter: null,
            perPage: 25,
            loading: false,
            
            // Table specific variables
            currentPage: 1,
            sortBy: 'code',
            sortDesc: false,
            
            // Modal specific variables
            modalTitle: 'Subsidiary Ledger List',
            pageOptions: [10, 25, 50, 100],
            filterOn: [],
            
            // Fields matching subsidiary-ledger-table.vue structure
            fields: [
                {
                    key: "code",
                    label: "Code",
                    sortable: true,
                    thClass: "text-center",
                    tdClass: "text-center font-weight-bold",
                },
                {
                    key: "slType",
                    label: "SL Type",
                    sortable: true,
                    thClass: "text-center",
                    tdClass: "text-center",
                },
                {
                    key: "client",
                    label: "Client",
                    sortable: true,
                    thClass: "text-center",
                    tdClass: "",
                },
                {
                    key: "projectName",
                    label: "Project Name",
                    sortable: true,
                    thClass: "text-center",
                    tdClass: "",
                },
                {
                    key: "contractNo",
                    label: "Contract No",
                    sortable: true,
                    thClass: "text-center",
                    tdClass: "text-center",
                },
                {
                    key: "validity",
                    label: "Validity",
                    sortable: true,
                    thClass: "text-center",
                    tdClass: "text-center",
                },
            ],
            
            tableBusy: false,
        };
    },
    computed: {
        filtered() {
            let data = this.subsidiaryLedgers;
            if (this.filter && this.filter.trim() !== "") {
                data = data.filter((item) => {
                    return item.filterKey
                        .toLowerCase()
                        .includes(this.filter.toLowerCase());
                });
            }
            return data;
        },
        processedData() {
            return this.filtered.map(item => ({
                ...item,
                slType: item.subsidiaryLedgerType,
                filterKey: `${item.code || ''} ${item.clientDto?.name || ''} ${item.projectName || ''} ${item.contractNumber || ''} ${item.subsidiaryLedgerType || ''}`.toLowerCase()
            }));
        },
        rows() {
            return this.filtered.length;
        },
    },
    methods: {
        formatValidity(validityDate) {
            if (!validityDate) return '-';
            try {
                const date = new Date(validityDate);
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit'
                });
            } catch (error) {
                return '-';
            }
        },
        onFiltered(filteredItems) {
            this.currentPage = 1;
        },
        
        getSLTypeName(slTypeId) {
            const slType = this.slTypes.find(type => type.id === slTypeId);
            return slType ? slType.name : 'Unknown';
        },
        
        formatDate(date) {
            if (!date) return '-';
            return new Date(date).toLocaleDateString();
        },
        
        dropData(data) {
            this.$emit('selectSubsidiaryLedger', data[0]);
            this.$bvModal.hide('sl-modal');
        },
        
        async fetchSubsidiaryLedgers() {
            this.tableBusy = true;
            this.loading = true;
            
            try {
                await this.$store.dispatch("sltype/getSLTypes")
                    .then((res) => {
                        if (res.data.error) {
                            this.showToast(res.data.errorMessage, "error");
                            return;
                        }
                        this.slTypes = res.data;
                    });
                const campusId = this.$store.state.data.selectedCampus.id;
                await this.$store.dispatch("subsidiaryledger/getSubsidiaryLedgersByCampus", campusId)
                    .then((res) => {
                        if (res.data.error) {
                            this.showToast(res.data.errorMessage, "error");
                            return;
                        }
                        this.subsidiaryLedgers = res.data.map(item => ({
                            ...item,
                            filterKey: `${item.code || ''} ${item.clientDto?.name || ''} ${item.projectName || ''} ${item.contractNumber || ''} ${item.subsidiaryLedgerType || ''}`.toLowerCase()
                        }));
                    });
            } catch (error) {
                this.showToast("Error fetching subsidiary ledgers", "error");
            } finally {
                this.tableBusy = false;
                this.loading = false;
            }
        }
    },
    async created() {
        await this.fetchSubsidiaryLedgers();
    },
};
</script>

<template>
    <b-modal 
        id="sl-modal" 
        :title="modalTitle" 
        size="xl" 
        title-class="font-18" 
        hide-footer 
        no-close-on-backdrop
        @show="fetchSubsidiaryLedgers"
    >
        <div class="row mt-1">
            <div class="col-sm-12 col-md-6">
                <div class="dataTables_filter">
                    <label class="d-inline-flex align-items-center">
                        Search:
                        <b-form-input
                            autocomplete="off"
                            v-model="filter"
                            type="search"
                            placeholder="Search subsidiary ledgers..."
                            class="form-control form-control-sm ms-2"
                        ></b-form-input>
                    </label>
                </div>
            </div>
            <div class="col-sm-12 col-md-6">
                <div class="d-flex justify-content-end align-items-center">
                    <label class="d-inline-flex align-items-center me-3">
                        Show:
                        <b-form-select
                            v-model="perPage"
                            :options="pageOptions"
                            class="form-select form-select-sm ms-2"
                            style="width: 80px;"
                        ></b-form-select>
                    </label>
                </div>
            </div>
        </div>
        <template v-if="loading">
            <div class="table-responsive mt-3">
                <table class="table table-bordered table-hover mb-0">
                    <thead class="table-light">
                        <tr>
                            <th class="text-center" v-for="field in fields" :key="field.key">
                                <b-skeleton width="80%" height="20px"></b-skeleton>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="i in 10" :key="i">
                            <td v-for="field in fields" :key="field.key" class="text-center">
                                <b-skeleton width="90%" height="25px"></b-skeleton>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
        <template v-else-if="!subsidiaryLedgers.length">
            <div class="text-center py-5">
                <div class="mb-3">
                    <i class="bx bx-folder-open font-size-48 text-muted"></i>
                </div>
                <h4 class="text-muted">No Subsidiary Ledgers Found</h4>
                <p class="text-muted">Create your first subsidiary ledger to get started.</p>
            </div>
        </template>
        <template v-else>
            <div class="table-responsive mb-0 mt-3">
                <b-table
                    :items="processedData"
                    :fields="fields"
                    :current-page="currentPage"
                    :per-page="perPage"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    class="table-bordered table-hover mb-0"
                    thead-class="table-light"
                    tbody-tr-class="align-middle"
                    show-empty
                    empty-text="No subsidiary ledgers found"
                    hover
                    select-mode="single"
                    selectable
                    @row-selected="dropData"
                    selected-variant="success"
                >
                    <template #cell(code)="data">
                        <div class="position-relative">
                            <RawData :data="data.item" v-if="$store.state.decrypt.isAdmin()" />
                            <span class="badge text-primary font-size-12">{{ data.item.code || '-' }}</span>
                        </div>
                    </template>

                    <template #cell(slType)="data">
                        <span class="badge bg-info font-size-12">{{ data.item.subsidiaryLedgerType }}</span>
                    </template>

                    <template #cell(client)="data">
                        <div class="text-truncate" :title="data.item.clientDto ? data.item.clientDto.name : '-'">
                            {{ data.item.clientDto ? data.item.clientDto.name : '-' }}
                        </div>
                    </template>

                    <template #cell(projectName)="data">
                        <div class="text-truncate" style="max-width: 250px;" :title="data.item.projectName">
                            {{ data.item.projectName || '-' }}
                        </div>
                    </template>

                    <template #cell(contractNo)="data">
                        <span class="font-weight-bold">{{ data.item.contractNumber || '-' }}</span>
                    </template>

                    <template #cell(validity)="data">
                        <div class="d-flex flex-column align-items-center">
                            <span>{{formatDateShort(new Date(data.item.validFromDate))}} - {{formatDateShort(new Date(data.item.validToDate))}}</span>
                        </div>
                    </template>
                </b-table>
            </div>
            <div class="d-flex justify-content-between align-items-center mt-3" v-if="rows > perPage">
                <div class="text-muted font-size-14">
                    Showing {{ ((currentPage - 1) * perPage) + 1 }} to {{ Math.min(currentPage * perPage, rows) }} of {{ rows }} entries
                </div>
                <div class="dataTables_paginate paging_simple_numbers">
                    <ul class="pagination pagination-rounded mb-0">
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="rows"
                            :per-page="perPage"
                            size="sm"
                            class="mb-0"
                        ></b-pagination>
                    </ul>
                </div>
            </div>
        </template>

        <div class="modal-footer border-top pt-3 mt-3">
            <div class="d-flex justify-content-between w-100">
                <small class="text-muted">
                    <i class="bx bx-info-circle me-1"></i>
                    Click on a row to select a subsidiary ledger
                </small>
                <button 
                    type="button" 
                    class="btn btn-secondary btn-sm" 
                    @click="$bvModal.hide('sl-modal')"
                >
                    Close
                </button>
            </div>
        </div>
    </b-modal>
</template>

<style scoped>
.text-truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.table-hover tbody tr:hover {
    background-color: rgba(0, 123, 255, 0.05);
}

.badge {
    font-size: 0.75rem;
}

.font-size-10 {
    font-size: 0.625rem;
}

.font-size-12 {
    font-size: 0.75rem;
}

.font-size-14 {
    font-size: 0.875rem;
}

.font-size-48 {
    font-size: 3rem;
}
</style>